import { inject, Injectable } from '@angular/core';
import { QuoteToolLiteStore, SharedDataStore } from '../store';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DateRange } from '@lx/shared/data-access/types';
import { DateFormatService } from '@lx/shared/i18n';
import { getDateFromString, isDateWithinRange } from '@lx/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class ChristmasClosureDisclaimerService {
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly dateFormatService = inject(DateFormatService);

  getDisclaimerForSelectedYear(year: number): Observable<string> {
    return this.getClosureDatesForSelectedYear(year).pipe(map((closures) => this.formatDisclaimer(closures)));
  }

  getDisclaimerForSelectedDates(): Observable<string> {
    return combineLatest([this.quoteToolLiteStore.selectedDates$, this.getClosureDatesForSelectedSchool()]).pipe(
      map(([selectedDateRange, closureDateRanges]) => {
        const { selectedStartDate, selectedEndDate } = selectedDateRange ?? {};

        if (!selectedStartDate || !selectedEndDate || closureDateRanges.length === 0) {
          return '';
        }

        const userRange: DateRange = {
          startDate: selectedStartDate,
          endDate: selectedEndDate,
        };

        const overlappingClosures = closureDateRanges.filter((closureRange) => this.filterSelectedDatesOverlapClosure(userRange, closureRange));

        return overlappingClosures.length > 0 ? this.formatDisclaimer(overlappingClosures) : '';
      }),
      shareReplay(1),
    );
  }

  private getClosureDatesForSelectedYear(year: number): Observable<DateRange[]> {
    return this.getClosureDatesForSelectedSchool().pipe(
      map((closures) =>
        closures.filter(({ startDate, endDate }) => {
          if (!startDate || !endDate) {
            return false;
          }

          const startYear = getDateFromString(startDate).getFullYear();
          const endYear = getDateFromString(endDate).getFullYear();

          return year >= startYear && year <= endYear;
        }),
      ),
    );
  }

  private getClosureDatesForSelectedSchool(): Observable<DateRange[]> {
    return combineLatest([this.sharedDataStore.schools$, this.quoteToolLiteStore.selectedSchoolLocation$]).pipe(
      map(([schools, selectedSchoolLocation]) => {
        if (!schools?.length || !selectedSchoolLocation?.schoolCode) {
          return [];
        }

        const selectedSchool = schools.find((school) => school.code === selectedSchoolLocation.schoolCode);

        return selectedSchool?.christmasClosures ?? [];
      }),
      shareReplay(1),
    );
  }

  private formatDisclaimer(closures: DateRange[]): string {
    if (!closures?.length) {
      return '';
    }

    const ranges = closures
      .map(({ startDate, endDate }) => {
        if (!startDate || !endDate) {
          return '';
        }
        const start = this.dateFormatService.formatDate(startDate, 'dd–MM–yyyy');
        const end = this.dateFormatService.formatDate(endDate, 'dd–MM–yyyy');

        return start && end ? $localize`${start} to ${end}` : '';
      })
      .filter(Boolean);

    if (ranges.length === 0) {
      return '';
    }

    const joinedRanges = ranges.length === 1 ? ranges[0] : ranges.join($localize` and `);

    return $localize`Please note that the school will be closed from ${joinedRanges} for the Christmas break.`;
  }

  private filterSelectedDatesOverlapClosure(selectedRange: DateRange, closureRange: DateRange): boolean {
    const { startDate: selectedStartStr, endDate: selectedEndStr } = selectedRange;
    const { startDate: closureStartStr, endDate: closureEndStr } = closureRange;

    if (!selectedStartStr || !selectedEndStr || !closureStartStr || !closureEndStr) {
      return false;
    }

    const selectedStart = getDateFromString(selectedStartStr);
    const selectedEnd = getDateFromString(selectedEndStr);
    const closureStart = getDateFromString(closureStartStr);
    const closureEnd = getDateFromString(closureEndStr);

    const selectedRangeTuple: [Date, Date] = [selectedStart, selectedEnd];
    const closureRangeTuple: [Date, Date] = [closureStart, closureEnd];

    return (
      isDateWithinRange(selectedStart, closureRangeTuple) ||
      isDateWithinRange(selectedEnd, closureRangeTuple) ||
      isDateWithinRange(closureStart, selectedRangeTuple) ||
      isDateWithinRange(closureEnd, selectedRangeTuple)
    );
  }
}
